<template>
	<div class="pdf-viewer">
		<div class="pdf-viewer__toolbar">
			<div class="pdf-viewer__meta">
				<span class="pdf-viewer__badge">
					<i class="mdi mdi-file-pdf-box"></i>
					PDF
				</span>
				<span
					class="pdf-viewer__name"
					:title="fileName"
				>{{ fileName }}</span>
			</div>
			<div class="pdf-viewer__actions">
				<PButton
					icon="pi pi-external-link"
					label="Open"
					size="small"
					severity="secondary"
					outlined
					@click="openInNewTab"
				/>
				<PButton
					icon="pi pi-download"
					label="Download"
					size="small"
					@click="download"
				/>
			</div>
		</div>

		<div class="pdf-viewer__stage">
			<iframe
				:key="pdfUrl"
				:src="pdfUrl"
				class="pdf-viewer__iframe"
				title="PDF document"
				@load="onLoaded"
			/>
			<div
				v-if="loading"
				class="pdf-viewer__loading"
			>
				<PProgressSpinner
					style="width: 2.25rem; height: 2.25rem;"
					strokeWidth="4"
				/>
				<span>Loading document…</span>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent } from 'vue'

	export default defineComponent({
		name: 'WPdfViewer',
		props: {
			pdfUrl: {
				type: String,
				required: true,
			},
			title: {
				type: String,
				default: '',
			},
		},
		data() {
			return {
				loading: true,
			}
		},
		computed: {
			fileName(): string {
				if (this.title) return this.title
				const path = (this.pdfUrl || '').split('?')[0]
				const leaf = path.split('/').filter(Boolean).pop() || 'document.pdf'
				return decodeURIComponent(leaf)
			},
		},
		watch: {
			pdfUrl() {
				this.loading = true
			},
		},
		methods: {
			onLoaded() {
				this.loading = false
			},
			openInNewTab() {
				window.open(this.pdfUrl, '_blank', 'noopener,noreferrer')
			},
			download() {
				const link = document.createElement('a')
				link.href = this.pdfUrl
				link.download = this.fileName.endsWith('.pdf')
					? this.fileName
					: `${this.fileName}.pdf`
				link.rel = 'noopener'
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
			},
		},
	})
</script>

<style scoped>
.pdf-viewer {
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
	background: var(--surface-ground);
}

.pdf-viewer__toolbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 0.65rem;
	padding: 0.65rem 0.85rem;
	border-bottom: 1px solid var(--surface-border);
	background:
		linear-gradient(
			180deg,
			var(--surface-card) 0%,
			color-mix(in srgb, var(--surface-ground) 55%, var(--surface-card)) 100%
		);
}

.pdf-viewer__meta {
	display: flex;
	align-items: center;
	gap: 0.65rem;
	min-width: 0;
}

.pdf-viewer__badge {
	display: inline-flex;
	align-items: center;
	gap: 0.3rem;
	padding: 0.2rem 0.5rem;
	border-radius: 6px;
	background: color-mix(in srgb, var(--red-500, #ef4444) 12%, transparent);
	color: var(--red-600, #dc2626);
	font-size: 0.72rem;
	font-weight: 700;
	letter-spacing: 0.04em;
	flex-shrink: 0;
}

.pdf-viewer__badge i {
	font-size: 0.95rem;
}

.pdf-viewer__name {
	font-size: 0.9rem;
	font-weight: 600;
	color: var(--text-color);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.pdf-viewer__actions {
	display: flex;
	align-items: center;
	gap: 0.45rem;
	flex-shrink: 0;
}

.pdf-viewer__stage {
	position: relative;
	flex: 1 1 auto;
	min-height: 0;
	background:
		repeating-linear-gradient(
			-45deg,
			var(--surface-ground),
			var(--surface-ground) 10px,
			color-mix(in srgb, var(--surface-border) 35%, var(--surface-ground)) 10px,
			color-mix(in srgb, var(--surface-border) 35%, var(--surface-ground)) 20px
		);
}

.pdf-viewer__iframe {
	display: block;
	width: 100%;
	height: 100%;
	min-height: 560px;
	border: 0;
	background: var(--surface-card);
}

.pdf-viewer__loading {
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.75rem;
	background: color-mix(in srgb, var(--surface-ground) 88%, transparent);
	color: var(--text-color-secondary);
	font-size: 0.88rem;
	pointer-events: none;
}
</style>
